import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, Moon, Sun, Heart, Eye, Users, Cloud, Gift, 
  Baby, Flame, Theater, HeartPulse, Target, CloudMoon, Globe,
  HeartCrack, HandHeart, Palette, Feather, Sparkles,
  Infinity, SunMoon, Lock, Check, Award, Crown
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";

const iconMap = {
  'moon': Moon,
  'sun': Sun,
  'heart': Heart,
  'eye': Eye,
  'users': Users,
  'cloud': Cloud,
  'gift': Gift,
  'baby': Baby,
  'flame': Flame,
  'mask': Theater,
  'heart-pulse': HeartPulse,
  'target': Target,
  'cloud-moon': CloudMoon,
  'globe': Globe,
  'heart-crack': HeartCrack,
  'hand-heart': HandHeart,
  'palette': Palette,
  'feather': Feather,
  'yin-yang': Moon,
  'sparkles': Sparkles,
  'infinity': Infinity,
  'sun-moon': SunMoon
};

const ShadowSelfPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState({ completed: 0, total: 22, percentage: 0 });
  const [isPremium, setIsPremium] = useState(false);
  const [hasBadge, setHasBadge] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/shadow-self/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModules(response.data.modules);
      setProgress(response.data.progress);
      setIsPremium(response.data.is_premium);
      setHasBadge(response.data.has_completion_badge);
    } catch (error) {
      console.error("Error fetching modules:", error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Moon;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-teal-500/20 flex items-center justify-center">
            <Moon className="w-8 h-8 text-teal-600" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="shadow-self-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Moon className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Shadow Self</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <Moon className="w-10 h-10 text-teal-600" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Accepting our Shadow Self
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-2">
            with Love and Empathy
          </p>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            22 sessions of evidence-based shadow integration work. Learn to embrace 
            all parts of yourself with compassion and understanding.
          </p>
        </div>

        {/* Progress Section */}
        <Card className="glass-card rounded-2xl border-0 mb-10 bg-gradient-to-r from-teal-50/50 to-emerald-50/50 dark:from-teal-950/20 dark:to-emerald-950/20">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-teal-500/20 flex items-center justify-center">
                  {hasBadge ? (
                    <Award className="w-7 h-7 text-teal-600" />
                  ) : (
                    <Moon className="w-7 h-7 text-teal-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-foreground">
                    {hasBadge ? "🌓 Shadow Integration Mastery!" : "Your Progress"}
                  </h3>
                  <p className="text-muted-foreground">
                    {progress.completed} of {progress.total} sessions completed
                  </p>
                </div>
              </div>
              <div className="w-full md:w-64">
                <Progress value={progress.percentage} className="h-3 bg-teal-100 dark:bg-teal-900/30" />
                <p className="text-sm text-muted-foreground text-center mt-2">{progress.percentage}% complete</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Premium CTA for non-premium users */}
        {!isPremium && (
          <Card className="glass-card rounded-2xl border-0 mb-10 bg-gradient-to-r from-primary/10 to-accent/10">
            <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Crown className="w-10 h-10 text-primary" />
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Unlock All 22 Sessions
                  </h3>
                  <p className="text-muted-foreground">
                    7 sessions are free. Go premium to access all 22 and complete your shadow integration journey!
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate("/subscription")} className="btn-primary">
                Go Premium
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Introduction Quote */}
        <Card className="glass-card rounded-2xl border-0 mb-10 bg-teal-50/30 dark:bg-teal-950/10">
          <CardContent className="p-6 text-center">
            <p className="text-lg italic text-foreground mb-2">
              "Until you make the unconscious conscious, it will direct your life and you will call it fate."
            </p>
            <span className="text-sm text-teal-600">— Carl Jung</span>
          </CardContent>
        </Card>

        {/* Free Sessions */}
        <div className="mb-10">
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <span className="text-teal-600">🌑</span> Foundation Sessions (Free)
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {modules.filter(m => !m.is_premium).map((module) => {
              const Icon = getIcon(module.icon);
              return (
                <Card 
                  key={module.id}
                  className={`glass-card rounded-2xl border-0 cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
                    module.is_completed ? 'ring-2 ring-teal-500/50 bg-teal-50/30 dark:bg-teal-950/20' : ''
                  }`}
                  onClick={() => navigate(`/shadow-self/${module.id}`)}
                  data-testid={`shadow-module-${module.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        module.is_completed ? 'bg-teal-500/20' : 'bg-teal-100 dark:bg-teal-900/30'
                      }`}>
                        {module.is_completed ? (
                          <Check className="w-6 h-6 text-teal-600" />
                        ) : (
                          <Icon className="w-6 h-6 text-teal-600" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-muted-foreground">Session {module.id}</span>
                          <span className="text-xs text-muted-foreground">• {module.estimated_time}</span>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-foreground mb-1">
                          {module.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {module.subtitle}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Premium Sessions */}
        <div>
          <h2 className="font-heading text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
            <Crown className="w-6 h-6 text-primary" /> Deep Work Sessions (Premium)
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {modules.filter(m => m.is_premium).map((module) => {
              const Icon = getIcon(module.icon);
              return (
                <Card 
                  key={module.id}
                  className={`glass-card rounded-2xl border-0 transition-all duration-300 ${
                    module.is_locked 
                      ? 'opacity-70 cursor-not-allowed' 
                      : 'cursor-pointer hover:scale-[1.02]'
                  } ${module.is_completed ? 'ring-2 ring-teal-500/50 bg-teal-50/30 dark:bg-teal-950/20' : ''}`}
                  onClick={() => !module.is_locked && navigate(`/shadow-self/${module.id}`)}
                  data-testid={`shadow-module-${module.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        module.is_completed 
                          ? 'bg-teal-500/20' 
                          : module.is_locked 
                            ? 'bg-muted' 
                            : 'bg-teal-100 dark:bg-teal-900/30'
                      }`}>
                        {module.is_completed ? (
                          <Check className="w-6 h-6 text-teal-600" />
                        ) : module.is_locked ? (
                          <Lock className="w-6 h-6 text-muted-foreground" />
                        ) : (
                          <Icon className="w-6 h-6 text-teal-600" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-muted-foreground">Session {module.id}</span>
                          <span className="text-xs text-muted-foreground">• {module.estimated_time}</span>
                          {module.is_locked && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                              Premium
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-foreground mb-1">
                          {module.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {module.subtitle}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Completion Message */}
        {hasBadge && (
          <Card className="glass-card rounded-2xl border-0 mt-10 bg-gradient-to-r from-teal-500/10 to-emerald-500/10 text-center">
            <CardContent className="p-8">
              <div className="w-20 h-20 rounded-full bg-teal-500/20 flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-teal-600" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-foreground mb-2">
                🌓 Congratulations!
              </h3>
              <p className="text-muted-foreground">
                You've completed all 22 Shadow Self sessions and earned your Shadow Integration Mastery Badge!
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ShadowSelfPage;
